import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguageSwitch } from '@/hooks/useLanguage';
import { useCart } from '@/contexts/CartContext';
import { useToast } from '@/contexts/ToastContext';
import { useProducts } from '@/contexts/ProductsContext';
import { fetchPublicStock, stockForSlug, type StockBySku } from '@/lib/publicStock';
import { fetchApprovedReviews } from '@/lib/productReviewsApi';
import { applyDiscountToPrice } from '@/lib/discountApi';
import { trackViewContent, trackAddToCart } from '@/lib/tracking';
import type { ProductReview } from '@/types/product';
import SharedNavbar from '@/components/feature/SharedNavbar';
import SharedFooter from '@/components/feature/SharedFooter';
import { Seo } from '@/components/Seo';
import StockBadge from '@/components/feature/StockBadge';
import ImageGallery from './ImageGallery';
import SizeGuide from './SizeGuide';
import VideoPlaceholder from './VideoPlaceholder';
import IngredientDetailCards from './IngredientDetailCards';
import ClinicalResultsChart from './ClinicalResultsChart';
import ProductReviews from './ProductReviews';
import ProductFAQ from './ProductFAQ';
import CrossSell from './CrossSell';
import { useSiteDesign } from '@/hooks/useSiteDesign';
import ProductV2 from '@/shared/rxk-product/ProductV2';
import { RxkProductHost } from '../RxkProductHost';

export default function ProductDetailPage() {
  const { slug } = useParams();
  const { t } = useTranslation();
  const { currentLang } = useLanguageSwitch();
  const prefix = currentLang === 'ko' ? '/ko' : currentLang === 'bn' ? '/bn' : '';
  const { addToCart, openCart } = useCart();
  const { showToast } = useToast();
  const { products, loading: productsLoading, autoDiscount } = useProducts();
  const { isNew } = useSiteDesign();

  const product = products.find((p) => p.id === slug);
  const related = products.filter((p) => p.id !== slug).slice(0, 2);

  const [activeTab, setActiveTab] = useState<'ingredients' | 'howToUse'>('ingredients');
  const [stock, setStock] = useState<StockBySku>({});
  const [customerReviews, setCustomerReviews] = useState<ProductReview[]>([]);

  useEffect(() => {
    fetchPublicStock().then(setStock);
  }, []);

  useEffect(() => {
    if (product?.sku) fetchApprovedReviews(product.sku).then(setCustomerReviews);
  }, [product?.sku]);

  useEffect(() => {
    if (!product) return;
    trackViewContent({ id: product.id, name: product.name, price: Number(product.price.replace(/[^0-9.]/g, '')) });
  }, [product]);

  if (!product && productsLoading) {
    return <div className="w-full min-h-screen bg-white" />;
  }

  if (!product) {
    return (
      <div className="w-full min-h-screen bg-white flex items-center justify-center">
        <div className="text-center flex flex-col gap-4">
          <h1 className="font-serif text-3xl font-bold text-[#2A2A2A]">Product Not Found</h1>
          <Link to={prefix + '/products'} className="text-sm text-[#7B1113] hover:underline">
            {t('products.seeAll')}
          </Link>
        </div>
      </div>
    );
  }

  const stockQty = stockForSlug(product.id, stock);
  const outOfStock = stockQty !== null && stockQty <= 0;

  const handleAddToCart = (qty = 1) => {
    if (outOfStock) return;
    addToCart(product.id, qty);
    trackAddToCart({ id: product.id, name: product.name, price: Number(product.price.replace(/[^0-9.]/g, '')), quantity: qty });
    showToast(
      t('cart.added', 'Added to Cart \u2713'),
      t('cart.viewCart', 'View Cart \u2192'),
      openCart,
    );
  };

  const canonicalUrl = `https://rxk.thekseoul.com/products/${product.id}`;
  const numericPrice = Number(product.price.replace(/[^0-9.]/g, ''));
  const discountedPrice = autoDiscount ? applyDiscountToPrice(numericPrice, autoDiscount, product.sku) : null;
  const metaTitle = `${product.name} | Rx.K Seoul`;
  const metaDescription = product.description || product.subtitle;

  const allReviews = [...customerReviews, ...product.reviews];
  const avgRating = allReviews.length > 0 ? allReviews.reduce((sum, r) => sum + r.rating, 0) / allReviews.length : 0;

  const productSchema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    image: [product.image, ...product.galleryImages].filter(Boolean),
    description: product.description,
    sku: product.sku,
    brand: { '@type': 'Brand', name: 'Rx.K Seoul' },
    offers: {
      '@type': 'Offer',
      url: canonicalUrl,
      priceCurrency: 'BDT',
      price: discountedPrice ?? numericPrice,
      availability: outOfStock ? 'https://schema.org/OutOfStock' : 'https://schema.org/InStock',
    },
    ...(allReviews.length > 0 && {
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: avgRating.toFixed(1),
        reviewCount: allReviews.length,
      },
      review: allReviews.slice(0, 10).map((r) => ({
        '@type': 'Review',
        reviewRating: { '@type': 'Rating', ratingValue: r.rating, bestRating: 5 },
        author: { '@type': 'Person', name: r.name },
        reviewBody: r.text,
      })),
    }),
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://rxk.thekseoul.com/' },
      { '@type': 'ListItem', position: 2, name: 'Products', item: 'https://rxk.thekseoul.com/products' },
      { '@type': 'ListItem', position: 3, name: product.name, item: canonicalUrl },
    ],
  };

  return (
    <>
      <Seo
        title={metaTitle}
        description={metaDescription}
        image={product.image}
        imageAlt={product.imageAlt}
        ogType="product"
        jsonLd={[productSchema, breadcrumbSchema]}
      />

      <SharedNavbar />
      {isNew ? (
        <main className="w-full min-h-screen bg-white">
          <RxkProductHost>
            <ProductV2 product={product} outOfStock={outOfStock} reviews={customerReviews} onAddToCart={handleAddToCart} />
          </RxkProductHost>
        </main>
      ) : (
      <main className="w-full min-h-screen bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 pt-24 pb-20">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-[11px] text-[#8A8A8A] font-light mb-8">
            <Link to={prefix + '/products'} className="hover:text-[#7B1113] transition-colors">
              {t('products.allProducts')}
            </Link>
            <span>/</span>
            <span className="text-[#2A2A2A]">{product.name}</span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Left: Image Gallery */}
            <ImageGallery
              mainImage={product.image}
              images={product.galleryImages}
              productName={product.name}
              badge={<StockBadge qty={stockQty} />}
            />

            {/* Right: Product Info */}
            <div className="flex flex-col gap-6">
              <div className="flex items-center gap-2">
                <span className="text-[10px] tracking-[0.15em] uppercase text-white font-medium px-2.5 py-1 rounded-full bg-[#7B1113]">
                  {product.stepKeyword}
                </span>
              </div>

              <h1 className="font-serif text-3xl md:text-4xl font-bold text-[#2A2A2A] leading-tight">
                {product.name}
              </h1>
              <span className="text-sm text-[#8A8A8A] font-light">{product.subtitle}</span>

              <div className="flex flex-col gap-1.5">
                <div className="flex items-baseline gap-2.5 flex-wrap">
                  {discountedPrice !== null ? (
                    <>
                      <span className="text-2xl font-serif font-bold text-[#7B1113]">৳{discountedPrice.toLocaleString()}</span>
                      <span className="text-sm text-[#8A8A8A]/70 font-light line-through">{product.price}</span>
                    </>
                  ) : (
                    <span className="text-2xl font-serif font-bold text-[#2A2A2A]">{product.price}</span>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {discountedPrice !== null && (
                    <span className="text-[10px] tracking-[0.08em] uppercase px-2.5 py-1 rounded-full bg-[#7B1113]/10 text-[#7B1113] font-semibold">
                      {autoDiscount?.type === 'percent' ? `${autoDiscount.value}% OFF` : `৳${autoDiscount?.value} OFF`}
                    </span>
                  )}
                  <span className="text-sm text-[#8A8A8A] font-light">{product.volume}</span>
                </div>
              </div>

              <p className="text-sm text-[#8A8A8A] font-light leading-[1.8]">
                {product.description}
              </p>

              {/* Claims */}
              <div className="flex flex-wrap gap-2">
                {product.claims.map((claim) => (
                  <span
                    key={claim}
                    className="text-[10px] tracking-[0.08em] uppercase px-3 py-1.5 rounded-full bg-[#F0EFED] text-[#8A8A8A] font-medium"
                  >
                    {claim}
                  </span>
                ))}
              </div>

              {/* CTA */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mt-2">
                <button
                  onClick={() => handleAddToCart()}
                  disabled={outOfStock}
                  className="w-full sm:w-auto px-8 py-3.5 bg-[#7B1113] text-white text-xs tracking-[0.15em] uppercase rounded-full hover:bg-[#5C0D0F] transition-colors cursor-pointer text-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {outOfStock ? t('common.outOfStock', 'Out of Stock') : t('common.addToCart')}
                </button>
                <Link
                  to={prefix + '/stores'}
                  className="w-full sm:w-auto px-8 py-3.5 border border-[#2A2A2A]/20 text-[#2A2A2A] text-xs tracking-[0.15em] uppercase rounded-full hover:bg-[#2A2A2A] hover:text-white transition-all cursor-pointer text-center"
                >
                  {t('common.whereToBuy')}
                </Link>
              </div>

              {/* Tabs */}
              <div className="flex items-center gap-4 mt-4 pt-6 border-t border-[#F0EFED]">
                <button
                  onClick={() => setActiveTab('ingredients')}
                  className={`text-xs tracking-[0.1em] uppercase font-medium transition-colors cursor-pointer ${
                    activeTab === 'ingredients' ? 'text-[#7B1113]' : 'text-[#8A8A8A] hover:text-[#2A2A2A]'
                  }`}
                >
                  {t('products.ingredients')}
                </button>
                <button
                  onClick={() => setActiveTab('howToUse')}
                  className={`text-xs tracking-[0.1em] uppercase font-medium transition-colors cursor-pointer ${
                    activeTab === 'howToUse' ? 'text-[#7B1113]' : 'text-[#8A8A8A] hover:text-[#2A2A2A]'
                  }`}
                >
                  {t('products.howToUse')}
                </button>
              </div>

              {activeTab === 'ingredients' && (
                <div className="flex flex-col gap-3">
                  <p className="text-sm text-[#8A8A8A] font-light leading-[1.8]">
                    {product.inci}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {product.keyIngredients.map((ing) => (
                      <span
                        key={ing.name}
                        className="text-[10px] tracking-[0.08em] uppercase px-3 py-1.5 rounded-full bg-[#1B3A5C]/10 text-[#1B3A5C] font-medium"
                      >
                        {ing.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {activeTab === 'howToUse' && (
                <ol className="flex flex-col gap-2 list-decimal list-inside">
                  {product.howToUse.map((step, i) => (
                    <li key={i} className="text-sm text-[#8A8A8A] font-light leading-[1.7]">
                      {step}
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </div>

          {/* Additional Sections */}
          <div className="mt-16 flex flex-col gap-8">
            <SizeGuide volume={product.volume} productName={product.name} />
            <VideoPlaceholder videoUrl={product.videoUrl} />
            <IngredientDetailCards details={product.ingredientDetails} />
            {product.clinicalResults && product.clinicalResults.length > 0 && (
              <ClinicalResultsChart results={product.clinicalResults} />
            )}
            <ProductReviews sku={product.sku} skinTypes={product.skinTypes} reviews={product.reviews} />
            <ProductFAQ sku={product.sku} faqs={product.faqs} />
            <CrossSell currentSlug={product.id} />
          </div>

          {/* Related Products */}
          {related.length > 0 && (
            <div className="mt-20 pt-16 border-t border-[#F0EFED]">
              <h2 className="font-serif text-2xl font-bold text-[#2A2A2A] mb-8">
                {t('products.relatedProducts')}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {related.map((p) => (
                  <Link
                    key={p.id}
                    to={`${prefix}/products/${p.id}`}
                    className="flex items-center gap-5 p-5 rounded-xl bg-[#F0EFED] hover:bg-[#E8E8E6] transition-colors cursor-pointer"
                  >
                    <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0 bg-white">
                      <img
                        src={p.image}
                        alt={p.imageAlt}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <span className="text-xs font-medium text-[#2A2A2A]">{p.name}</span>
                      <span className="text-xs text-[#8A8A8A] font-light">{p.price}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
      )}
      <SharedFooter />
    </>
  );
}